
import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  User as UserIcon,
  LogOut,
  Settings,
  Library,
  Feather,
  LayoutDashboard,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/auth";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { NotificationsBell } from "@/components/notifications/NotificationsBell";

export const Header = () => {
  const { user, profile, isLoading, signOut, isAuthor, isAdmin } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const getInitials = (name: string | undefined | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Use the isAuthor and isAdmin properties directly from the useAuth hook
  const displayName = profile ? (profile.full_name || profile.username || "User") : "User";
  const username = profile ? (profile.username || displayName.toLowerCase().replace(/\s/g, "")) : "";
  const avatarUrl = profile ? profile.avatar_url : "";

  return (
    <header
      className={`sticky top-0 bg-white z-50 ${
        isScrolled ? "shadow-sm" : ""
      } transition-shadow`}
    >
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/" className="flex items-center">
            <span className="text-xl font-serif font-bold text-chapterly-purple">
              ChapterSeed
            </span>
          </Link>

          <nav className="hidden lg:flex items-center space-x-4">
            <Link to="/" className="text-gray-600 hover:text-chapterly-purple px-3 py-2 text-sm font-serif">
              Home
            </Link>
            <Link to="/browse" className="text-gray-600 hover:text-chapterly-purple px-3 py-2 text-sm font-serif">
              Browse
            </Link>
            <Link to="/categories" className="text-gray-600 hover:text-chapterly-purple px-3 py-2 text-sm font-serif">
              Categories
            </Link>
            {user && (
              <Link to="/library" className="text-gray-600 hover:text-chapterly-purple px-3 py-2 text-sm font-medium">
                Library
              </Link>
            )}
            {isAuthor && (
              <Link to="/author-dashboard" className="text-gray-600 hover:text-chapterly-purple px-3 py-2 text-sm font-medium">
                Author Dashboard
              </Link>
            )}
          </nav>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Search"
            onClick={() => navigate("/browse")}
          >
            <Search size={20} />
          </Button>

          {user && <NotificationsBell />}

          {isLoading ? (
            <Skeleton className="h-10 w-10 rounded-full" />
          ) : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative rounded-full h-8 w-8 p-0"
                  aria-label="User menu"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={avatarUrl} alt={displayName ? `${displayName}'s avatar` : 'User avatar'} />
                    <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
                  </Avatar>
                  {isAuthor && (
                    <Badge
                      className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center"
                      variant="outline"
                    >
                      <Feather size={10} />
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="flex items-center justify-start p-2">
                  <div className="flex flex-col">
                    <p className="text-sm font-medium">{displayName}</p>
                    <p className="text-xs text-gray-500 truncate">
                      @{username}
                    </p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile">
                    <UserIcon className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/library">
                    <Library className="mr-2 h-4 w-4" />
                    <span>Library</span>
                  </Link>
                </DropdownMenuItem>

                {isAuthor && (
                  <DropdownMenuItem asChild>
                    <Link to="/author-dashboard">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      <span>Author Dashboard</span>
                    </Link>
                  </DropdownMenuItem>
                )}

                {!isAuthor && (
                  <DropdownMenuItem asChild>
                    <Link to="/become-author">
                      <Feather className="mr-2 h-4 w-4" />
                      <span>Become Author</span>
                    </Link>
                  </DropdownMenuItem>
                )}

                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin-dashboard">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      <span>Admin Dashboard</span>
                    </Link>
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center space-x-2">
              <Button variant="ghost" asChild>
                <Link to="/auth?mode=login">Log in</Link>
              </Button>
              <Button asChild>
                <Link to="/auth?mode=signup">Sign up</Link>
              </Button>
            </div>
          )}

          <div className="lg:hidden">
            <button
              className="p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <div className="w-5 h-5 flex flex-col justify-between">
                <span
                  className={`block h-0.5 w-full bg-current transform transition ${
                    isMenuOpen
                      ? "rotate-45 translate-y-2"
                      : "rotate-0 translate-y-0"
                  }`}
                />
                <span
                  className={`block h-0.5 w-full bg-current transition ${
                    isMenuOpen ? "opacity-0" : "opacity-100"
                  }`}
                />
                <span
                  className={`block h-0.5 w-full bg-current transform transition ${
                    isMenuOpen
                      ? "-rotate-45 -translate-y-2"
                      : "rotate-0 translate-y-0"
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="lg:hidden px-4 py-2 border-t">
          <nav className="flex flex-col space-y-2">
            <NavLink
              to="/browse"
              className={({ isActive }) =>
                `p-2 rounded-md ${
                  isActive
                    ? "bg-gray-100 font-serif"
                    : "hover:bg-gray-50 transition-colors font-serif"
                }`
              }
              onClick={() => setIsMenuOpen(false)}
            >
              Browse
            </NavLink>

            <NavLink
              to="/categories"
              className={({ isActive }) =>
                `p-2 rounded-md ${
                  isActive
                    ? "bg-gray-100 font-serif"
                    : "hover:bg-gray-50 transition-colors font-serif"
                }`
              }
              onClick={() => setIsMenuOpen(false)}
            >
              Categories
            </NavLink>

            {user && (
              <NavLink
                to="/library"
                className={({ isActive }) =>
                  `p-2 rounded-md ${
                    isActive
                      ? "bg-gray-100 font-medium"
                      : "hover:bg-gray-50 transition-colors"
                  }`
                }
                onClick={() => setIsMenuOpen(false)}
              >
                Library
              </NavLink>
            )}

            {isAuthor ? (
              <NavLink
                to="/author-dashboard"
                className={({ isActive }) =>
                  `p-2 rounded-md ${
                    isActive
                      ? "bg-gray-100 font-medium"
                      : "hover:bg-gray-50 transition-colors"
                  }`
                }
                onClick={() => setIsMenuOpen(false)}
              >
                Author Dashboard
              </NavLink>
            ) : (
              user && (
                <NavLink
                  to="/become-author"
                  className={({ isActive }) =>
                    `p-2 rounded-md ${
                      isActive
                        ? "bg-gray-100 font-medium"
                        : "hover:bg-gray-50 transition-colors"
                    }`
                  }
                  onClick={() => setIsMenuOpen(false)}
                >
                  Become an Author
                </NavLink>
              )
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
